import { Box, Collapse, Typography, IconButton } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import type { SxProps, Theme } from "@mui/material/styles";
import { useCallback, useId, useState, type ReactNode } from "react";
import { IoChevronDown } from "react-icons/io5";

export type CollapsibleSectionCardProps = {
    title: ReactNode;
    children: ReactNode;
    /** @default false */
    defaultExpanded?: boolean;
    /** Optional id for aria-controls; section id is derived for a11y. */
    id?: string;
    sx?: SxProps<Theme>;
};

/**
 * Card with a full-width clickable header (title + primary chevron control on the right) and collapsible body.
 */
export const CollapsibleSectionCard = (props: CollapsibleSectionCardProps) => {
    const { title, children, defaultExpanded = false, id: idProp, sx } = props;
    const theme = useTheme();
    const reactId = useId();
    const sectionId = idProp ?? `collapsible-section-${reactId.replace(/:/g, "")}`;
    const headerId = `${sectionId}-header`;
    const regionId = `${sectionId}-region`;

    const [expanded, setExpanded] = useState(defaultExpanded);

    const toggle = useCallback(() => {
        setExpanded((e) => !e);
    }, []);

    return (
        <Box
            sx={{
                backgroundColor: theme.palette.background.default,
                borderRadius: "7px",
                overflow: "hidden",
                ...sx,
            }}
        >
            <Box
                id={headerId}
                aria-expanded={expanded}
                aria-controls={regionId}
                onClick={toggle}
                sx={{
                    width: "100%",
                    margin: 0,
                    padding: theme.spacing(3),
                    border: "none",
                    borderBottom: expanded ? `1px solid ${theme.palette.divider}` : "none",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    backgroundColor: "transparent",
                    textAlign: "left",
                    font: "inherit",
                    color: "inherit",
                    boxSizing: "border-box",
                }}
            >
                <Typography variant="h6" component="span" sx={{ flex: 1, minWidth: 0 }}>
                    {title}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'flex-start', flexShrink: 0 }}>
                    <IconButton
                        aria-label={expanded ? `Zbaliť sekciu: ${title}` : `Rozbaliť sekciu: ${title}`}
                        sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '7px',
                            backgroundColor: theme.palette.primary.main,
                            color: 'white',
                            '&:hover': {
                                backgroundColor: theme.palette.primary.dark,
                            },
                        }}
                    >
                        <IoChevronDown
                            size={16}
                            color="#ffffff"
                            style={{
                                transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                                transition: "transform 0.2s ease",
                            }}
                        />
                    </IconButton>
                </Box>


            </Box>

            <Collapse
                in={expanded}
                timeout="auto"
                unmountOnExit={false}
                sx={{
                    overflow: "hidden",
                    width: "100%",
                    maxWidth: "100%",
                }}
            >
                <Box
                    id={regionId}
                    role="region"
                    aria-labelledby={headerId}
                    sx={{
                        overflow: "hidden",
                        width: "100%",
                        maxWidth: "100%",
                    }}
                >
                    {children}
                </Box>
            </Collapse>
        </Box>
    );
};
